import { motion } from 'framer-motion'
import { FileText, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const Terms = () => {
  const { t } = useTranslation()
  const lastUpdated = '06.07.2026'

  return (
    <div className="pt-16 overflow-x-hidden">
      <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-6">
              <FileText className="w-4 h-4 mr-2" />
              {t('termsPage.title')}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('termsPage.title')} <span className="gradient-text">{t('termsPage.headline')}</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('termsPage.description')}
            </p>
            <p className="text-sm text-gray-500 mt-4">{t('termsPage.lastUpdated')}: {lastUpdated}</p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 space-y-10">

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                
                <div className="prose prose-lg max-w-none">
                  <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-lg p-6 my-6">
                    <h3 className="font-semibold text-primary-900 mb-3">{t('termsPage.agreement.title')}</h3>
                    <p className="text-primary-800 leading-relaxed">
                      {t('termsPage.agreement.description')}
                    </p>
                  </div>
                  
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 my-6">
                    <p className="text-green-800">
                      {t('termsPage.agreement.acceptance')}
                    </p>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed">
                    {t('termsPage.agreement.purpose')}
                  </p>
                  
                  <div className="bg-blue-50 rounded-lg p-4 my-6">
                    <p className="text-blue-800">
                      {t('termsPage.agreement.contactInfo')}
                    </p>
                    <div className="mt-3 text-blue-800">
                      <p className="font-semibold">{t('termsPage.contact.company')}</p>
                      <p>{t('termsPage.contact.address')}</p>
                      <p>{t('termsPage.contact.district')}</p>
                      <p>{t('termsPage.contact.city')}</p>
                      <p>{t('termsPage.contact.country')}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-accent-600 rounded-full"></div>
                      </div>
                      <p className="text-gray-700">{t('termsPage.agreement.updates')}</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-accent-600 rounded-full"></div>
                      </div>
                      <p className="text-gray-700">{t('termsPage.agreement.review')}</p>
                    </div>
                  </div>
                </div>
              </motion.div>


              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">1</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section1.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                      {t('termsPage.section1.description')}
                    </p>

                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('termsPage.section1.vacanciesProjects')}
                      </p>
                    </div>
                    
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                      <p className="text-yellow-800">
                        {t('termsPage.section1.jurisdiction')}
                      </p>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('termsPage.section1.compliance')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">2</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section2.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                      <p className="text-purple-800">
                        {t('termsPage.section2.ownership')}
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('termsPage.section2.restrictions')}
                    </p>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-green-800">
                        {t('termsPage.section2.permittedUse')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">3</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section3.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('termsPage.section3.lawfulUse')}
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('termsPage.section3.noMisuse')}
                    </p>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('termsPage.section3.accurateInfo')}
                    </p>
                    
                    <div className="bg-red-50 border-l-4 border-red-500 p-4">
                      <p className="text-red-800">
                        {t('termsPage.section3.refuseCommunication')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">4</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section4.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-red-50 border-l-4 border-red-500 p-4">
                      <p className="text-red-800">
                        {t('termsPage.section4.agreement')}
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed mb-3">
                      {t('termsPage.section4.particularly')}
                    </p>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <ul className="space-y-2">
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                          <span className="text-gray-700">{t('termsPage.section4.interfere')}</span>
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                          <span className="text-gray-700">{t('termsPage.section4.unlawful')}</span>
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                          <span className="text-gray-700">{t('termsPage.section4.falseInfo')}</span>
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                          <span className="text-gray-700">{t('termsPage.section4.automated')}</span>
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                          <span className="text-gray-700">{t('termsPage.section4.maliciousFiles')}</span>
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                          <span className="text-gray-700">{t('termsPage.section4.unauthorized')}</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="bg-orange-50 rounded-lg p-4">
                      <p className="text-orange-800">
                        {t('termsPage.section4.restrictCommunication')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">5</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section5.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-green-50 border-l-4 border-green-500 p-4">
                      <p className="text-green-800">
                        {t('termsPage.section5.noPublicContent')}
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('termsPage.section5.contactMethods')}
                    </p>
                    
                    <div>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        {t('termsPage.section5.confirmation')}
                      </p>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <ul className="space-y-2">
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('termsPage.section5.accurate')}</span>
                          </li>
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('termsPage.section5.rightToShare')}</span>
                          </li>
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('termsPage.section5.noViolation')}</span>
                          </li>
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('termsPage.section5.resumeRights')}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('termsPage.section5.solePurpose')}
                      </p>
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-4">
                      <p className="text-yellow-800">
                        {t('termsPage.section5.noEmployment')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">6</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section6.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                      <p className="text-purple-800">
                        {t('termsPage.section6.feedbackUse')}
                      </p>
                    </div>
                    
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <p className="text-yellow-800">
                        {t('termsPage.section6.noOwnership')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">7</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section7.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                      {t('termsPage.section7.rightToAct')}
                    </p>
                    
                    <div className="bg-red-50 border-l-4 border-red-500 p-4">
                      <p className="text-red-800">
                        {t('termsPage.section7.restrictAccess')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">8</span>
                    </div>
                    <h2 className="text-lg sm:text-xl text-2xl font-bold text-gray-900 break-words">{t('termsPage.section8.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                      <p className="text-blue-800">
                        {t('termsPage.section8.privacyLink')}
                      </p>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-green-800">
                        {t('termsPage.section8.incorporation')}
                      </p>
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-4">
                      <p className="text-yellow-800">
                        {t('termsPage.section8.externalLinks')}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {t('termsPage.section8.operatedFrom')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">9</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section9.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('termsPage.section9.inEffect')}
                      </p>
                    </div>
                    
                    <div className="bg-red-50 border-l-4 border-red-500 p-4">
                      <p className="text-red-800">
                        {t('termsPage.section9.restrictAccess')}
                      </p>
                    </div>
                    
                    <div className="bg-orange-50 rounded-lg p-4">
                      <p className="text-orange-800">
                        {t('termsPage.section9.discontinueCommunication')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">10</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section10.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                      <p className="text-purple-800">
                        {t('termsPage.section10.updates')}
                      </p>
                    </div>
                    
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <p className="text-yellow-800">
                        {t('termsPage.section10.noGuarantee')}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {t('termsPage.section10.noResponsibility')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">11</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section11.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                      <p className="text-blue-800">
                        {t('termsPage.section11.law')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">12</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section12.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-green-50 border-l-4 border-green-500 p-4">
                      <p className="text-green-800">
                        {t('termsPage.section12.resolution')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">13</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section13.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                      {t('termsPage.section13.errors')}
                    </p>
                    
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                      <p className="text-purple-800">
                        {t('termsPage.section13.rightToCorrect')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">14</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section14.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                      <p className="text-yellow-800">
                        {t('termsPage.section14.asIs')}
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('termsPage.section14.accuracy')}
                    </p>
                    
                    <div className="bg-red-50 rounded-lg p-4">
                      <p className="text-red-800">
                        {t('termsPage.section14.noResponsibility')}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {t('termsPage.section14.userRisk')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.6, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">15</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section15.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-red-50 border-l-4 border-red-500 p-4">
                      <p className="text-red-800">
                        {t('termsPage.section15.noLiability')}
                      </p>
                    </div>
                    
                    <div className="bg-orange-50 rounded-lg p-4">
                      <p className="text-orange-800">
                        {t('termsPage.section15.userRisk')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.7, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">16</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section16.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('termsPage.section16.noViolation')}
                      </p>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-green-800">
                        {t('termsPage.section16.userResponsibility')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.8, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">17</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section17.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('termsPage.section17.privacyPolicy')}
                      </p>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-green-800">
                        {t('termsPage.section17.userResponsibility')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.9, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">18</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section18.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                      <p className="text-purple-800">
                        {t('termsPage.section18.agreement')}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {t('termsPage.section18.satisfaction')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.0, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">19</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('termsPage.section19.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('termsPage.section19.entireAgreement')}
                      </p>
                    </div>
                    
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <p className="text-yellow-800">
                        {t('termsPage.section19.unenforceable')}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {t('termsPage.section19.noWaiver')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.1, duration: 0.6 }}
                className="bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl p-8 text-white"
              >
                <div className="flex items-center mb-6">
                  <Mail className="w-12 h-12 mr-4" />
                  <div>
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold">20</span>
                    </div>
                    <h2 className="text-3xl font-bold">{t('termsPage.section20.title')}</h2>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <p className="text-primary-100 leading-relaxed">
                    {t('termsPage.section20.description')}
                  </p>
                  
                  <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                    <p className="font-semibold text-white mb-3">{t('termsPage.contact.company')}</p>
                    <div className="space-y-1 text-primary-100">
                      <p>{t('termsPage.contact.address')}</p>
                      <p>{t('termsPage.contact.district')}</p>
                      <p>{t('termsPage.contact.city')}</p>
                      <p>{t('termsPage.contact.country')}</p>
                    </div>
                    <div className="mt-4 space-y-2">
                      <p className="text-primary-100">
                        <span className="font-semibold">Phone:</span> {t('termsPage.contact.phone')}
                      </p>
                      <p className="text-primary-100">
                        <span className="font-semibold">Email:</span> <a href="mailto:infotechprosubkg@gmail.com" className="text-white hover:text-primary-100 underline break-words">{t('termsPage.contact.email')}</a>
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>


            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Terms